import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus } from "lucide-react";
import { Reveal, MaskLines, SectionLabel } from "./Reveal";
import { useLang } from "../i18n";

const PICS = [
  ["/img/real1.jpg", "md:col-span-2 md:row-span-2"],
  ["/img/real4.jpg", "md:col-span-1 md:row-span-1"],
  ["/img/real3.jpg", "md:col-span-1 md:row-span-2"],
  ["/img/moto_r1.jpg", "md:col-span-1 md:row-span-1"],
  ["/img/moto_r2.jpg", "md:col-span-2 md:row-span-1"],
  ["/img/real2.jpg", "md:col-span-1 md:row-span-1"],
  ["/img/moto_r3.jpg", "md:col-span-1 md:row-span-1"],
  ["/img/poster_moto.jpg", "md:col-span-2 md:row-span-2"],
  ["/img/moto_r5.jpg", "md:col-span-2 md:row-span-2"],
];

export const Gallery = () => {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <section id="gallery" className="py-16 md:py-40 bg-[#0B0A08]" data-testid="gallery-section">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10">
        <Reveal><SectionLabel index="04">{t.gallery.label}</SectionLabel></Reveal>
        <h2 key={lang} className="mt-8 mb-14 font-display text-4xl sm:text-5xl lg:text-7xl text-[#F7F4EE]">
          <MaskLines lines={[t.gallery.title]} lineClass="italic" />
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[160px] sm:auto-rows-[220px] md:auto-rows-[240px] gap-3 md:gap-4">
          {PICS.map(([src, span], i) => (
            <motion.button
              key={src}
              onClick={() => setOpen(src)}
              data-testid={`gallery-item-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.9, delay: (i % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className={`group relative overflow-hidden bg-[#1A1713] ${i === 0 ? "col-span-2 row-span-2" : ""} ${span}`}
            >
              <img src={src} alt="TSM WAY Sahara" loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-110" />
              <span className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-500" />
              <span className="absolute bottom-3 right-3 w-10 h-10 rounded-full border border-[#F3E5AB]/60 text-[#F3E5AB] flex items-center justify-center opacity-0 scale-75 group-hover:opacity-100 group-hover:scale-100 transition-[opacity,transform] duration-500">
                <Plus size={16} />
              </span>
            </motion.button>
          ))}
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/90 backdrop-blur-md flex items-center justify-center p-4 md:p-12"
            onClick={() => setOpen(null)} data-testid="gallery-lightbox"
          >
            <motion.img
              src={open} alt="TSM WAY" initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="max-h-full max-w-full object-contain"
            />
            <button className="absolute top-6 right-6 text-[#F3E5AB] flex items-center gap-2 font-mono text-xs uppercase tracking-widest" data-testid="gallery-lightbox-close">
              {t.gallery.close} <X size={18} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
